import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import {
  generateOutstandingAmountsForParMember,
  getJlgFirstAndLastEmiDates,
} from "../../../utils/utils";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";

const JlgParReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedOverdueFilter, setSelectedOverdueFilter] = useState(null); // State for overdue days filter
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null); // State for branch name filter
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null); // State for employee name filter
  const [fromDateDisbursed, setFromDateDisbursed] = useState("");
  const [toDateDisbursed, setToDateDisbursed] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Start early enough to catch every EMI in a loan's history. A later start date
  // silently drops any EMI falling before it, which throws off the amortisation.
  // Matches the Master reports and GplInterestReport.js.
  const fromDate = new Date("2000-01-01");
  const toDate = new Date();

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getJlgOutstandingReportData`,
          { params: { id } }
        );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  // Flatten the data for rendering
  const flattenedData = reportData
    .map((data) =>
      generateOutstandingAmountsForParMember(data, fromDate, toDate)
    ) // First map over the data
    .filter((result) => result !== null);



  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data?.branchName ?? "" },
    { label: "Branch Code", render: (data) => data?.branchCode ?? "" },
    {
      label: "Center Name",
      render: (data) => data.fk_member_details_belongsTo_center_centerId.name,
    },
    { label: "Customer Name", render: (data) => data?.memberName ?? "" },
    { label: "Customer ID", render: (data) => data?.customerId ?? "" },
    {
      label: "Address",
      render: (data) =>
        `${data.permanentDoorNo},${data.permanentStreetNo},${data.permanentPanchayat},${data.permanentTaluk},${data.permanentDistrict}-${data.permanentPincode}`,
    },
    { label: "Contact No", render: (data) => data?.phoneNumber ?? "" },
    { label: "Loan Type", render: (data) => (data ? data.loanType : "") },
    {
      label: "Sub Category",
      render: (data) => data?.businessDetails.natureOfBusiness ?? "",
    },
    { label: "Loan Cycle", render: (data) => data?.loanCycle ?? "" },
    { label: "Loan Account No", render: (data) => data?.loanId ?? "" },
    {
      label: "Rate of Interest",
      render: (data) => data?.proposedLoanDetails.rateOfInterest ?? "",
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data?.branchManagerStatus === "disbursed" &&
          data?.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }
          )
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getJlgFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-US") : "";
      },
    },
    {
      label: "Loan Amount",
      render: (data) =>
        data?.branchManagerStatus === "disbursed"
          ? data?.sanctionedLoanAmountBySanctionCommittee ?? ""
          : "",
    },
    { label: "Employee Name", render: (data) => data?.employeeName ?? "" },
    { label: "Prospect ID", render: (data) => data?.ApplicationId ?? "" },
    {
      label: "Total Outstanding Priciple",
      render: (data) => data?.totalOutstandingPrincipal ?? "",
    },
    // {
    //   label: "Total Outstanding Interest",
    //   render: (data) => data?.totalOutstandingInterest ?? "",
    // },
    {
      label: "Overdue Principal",
      render: (data) => data?.overduePrincipal ?? 0,
    },
    {
      label: "Overdue Interest",
      render: (data) => data?.overdueInterest ?? 0,
    },
    {
      label: "DPD",
      render: (data) => data?.overdueDays ?? 0,
    },
  ];

  // Extract unique branch names and employee names for dropdown options
  const branchOptions = [
    ...new Set(flattenedData.map((data) => data?.branchName)),
  ]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [
    ...new Set(flattenedData.map((data) => data?.username)),
  ]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  // Overdue days filter options
  const overdueFilters = [
    { value: "0-30", label: "0 - 30 days" },
    { value: "31-60", label: "31 - 60 days" },
    { value: "61-90", label: "61 - 90 days" },
    { value: "91-180", label: "91 - 180 days" },
    { value: "181-365", label: "181 - 365 days" },
    { value: ">365", label: "> 365 days" },
  ];

  // Function to filter the data based on selected filters
  const filterData = (data) => {
    let filteredData = data;

    if (selectedOverdueFilter) {
      filteredData = filteredData.filter((item) => {
        const overdueDays = item.overdueDays ?? 0;
        switch (selectedOverdueFilter.value) {
          case "0-30":
            return overdueDays >= 0 && overdueDays <= 30;
          case "31-60":
            return overdueDays >= 31 && overdueDays <= 60;
          case "61-90":
            return overdueDays >= 61 && overdueDays <= 90;
          case "91-180":
            return overdueDays >= 91 && overdueDays <= 180;
          case "181-365":
            // Starts at 181, not 180: a loan sitting on exactly 180 days used to
            // match both this bucket and "91-180" and was counted twice.
            return overdueDays >= 181 && overdueDays <= 365;
          case ">365":
            return overdueDays > 365;
          default:
            return data;
        }
      });
    }

    if (fromDateDisbursed && toDateDisbursed) {
      filteredData = filteredData.filter((item) => {
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return (
          updatedAt >= new Date(fromDateDisbursed) &&
          updatedAt <= new Date(toDateDisbursed)
        );
      });
    }

    if (selectedBranchFilter) {
      filteredData = filteredData.filter(
        (item) => item?.branchName === selectedBranchFilter.value
      );
    }

    if (selectedEmployeeFilter) {
      filteredData = filteredData.filter(
        (item) => item?.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filteredData = filteredData.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    return filteredData;
  };

  const allFilteredData = filterData(flattenedData);
  const totalPages = Math.ceil(allFilteredData.length / itemsPerPage);
  const filteredData = allFilteredData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">JLG PAR Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Overdue Days</Form.Label>
                  <Select
                    value={selectedOverdueFilter}
                    onChange={(val) => {
                      setSelectedOverdueFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && selectedBranchFilter && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={overdueFilters}
                    isClearable
                    placeholder="Filter by Overdue Days"
                    className="filter-dropdown"
                  />
                </Col>

                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDateDisbursed && toDateDisbursed && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>

                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={fromDateDisbursed}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDateDisbursed(val);
                        if (val && toDateDisbursed && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={toDateDisbursed}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDateDisbursed(val);
                        if (fromDateDisbursed && val && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={filteredData}
                reportName="JlgParReport"
                excelData={allFilteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={allFilteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JlgParReport;
